<template>
    <canvas ref="canvas" style="display:block; width:100%; height:100%;"></canvas>
</template>

<script>

// Renders a cropped + brightness-adjusted preview of an image by drawing it
// straight into a canvas and manually adjusting each pixel's RGB (clamped
// 0-255) - the exact same operation the backend's Intervention/GD pipeline
// runs (IMG_FILTER_BRIGHTNESS) - rather than approximating it with a CSS/SVG
// filter, which depends on the browser's own color pipeline (gamma handling,
// GPU compositing) and isn't guaranteed to match pixel-for-pixel.
export default {
    name: 'ImageCropPreview',

    props: {
        src: {
            type: String,
            required: true
        },
        coordinates: {
            type: Object,
            required: true
        },
        brightness: {
            type: Number,
            default: 0
        }
    },

    data () {
        return {
            image: null
        }
    },

    watch: {
        src: {
            immediate: true,
            handler: 'loadImage'
        },

        coordinates: {
            deep: true,
            handler: 'render'
        },

        brightness () {
            this.render()
        }
    },

    methods: {
        loadImage () {
            const img = new Image()
            img.onload = () => {
                this.image = img
                this.render()
            }
            img.src = this.src
        },

        render () {
            if (!this.image || !this.coordinates || !this.coordinates.width) {
                return
            }

            const canvas = this.$refs.canvas
            const width = Math.round(this.coordinates.width)
            const height = Math.round(this.coordinates.height)

            canvas.width = width
            canvas.height = height

            const ctx = canvas.getContext('2d')

            ctx.clearRect(0, 0, width, height)
            ctx.drawImage(
                this.image,
                this.coordinates.left,
                this.coordinates.top,
                this.coordinates.width,
                this.coordinates.height,
                0, 0, width, height
            )

            const offset = Math.round(this.brightness * 2.55)
            if (offset === 0) {
                return
            }

            const imageData = ctx.getImageData(0, 0, width, height)
            const data = imageData.data

            for (let i = 0; i < data.length; i += 4) {
                data[i] = Math.min(255, Math.max(0, data[i] + offset))
                data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + offset))
                data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + offset))
            }

            ctx.putImageData(imageData, 0, 0)
        }
    }
}
</script>
